import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Output, SimpleChanges, OnInit, OnDestroy, OnChanges, Inject, Input, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { debounceTime, distinctUntilChanged, EMPTY, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { EpsgIndex } from '../../../../shared/models/epsgData';
import epsgIndexData from 'epsg-index/all.json';
import { Site } from '../../../../shared/models/site';
import { CurrentStateService } from '../../../../core/services/current-state.service';
import { FonctionsService } from '../../../../shared/services/utilities/fonctions.service';
import { validDateValidator } from '../../../../shared/validators/valid-date.validator';
import { MENU_OPTIONS } from '../../../../shared/constants/left-menu-option';
import { PopupCloseMode } from '../../../control-points/models/popupMode';
import { EventData } from '../../models/event-data';
import { EventProjet } from '../../models/event-projet';
import { EventService } from '../../services/event-service';
import { ApiResponse } from '../../../../shared/models/api-response';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomCoordinate } from '../../../../shared/models/customCoordinate';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DraggableDirective } from '../../../../shared/directive/draggable';
import { EventTypes } from '../../models/event-type';
const EPSG_REFERENCE_OL: string = 'EPSG:3857';

interface SelectedEpsg {
  id: number;
  text: string;
}

@Component({
  selector: 'event-modal.component',
  imports: [
    CommonModule,
    NgSelectComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DraggableDirective
  ],
  templateUrl: './event-modal.component.html',
  styleUrls: ['./event-modal.component.scss']
})
export class EventModalComponent implements OnInit, OnDestroy {
  protected site: Site | undefined;
  protected event!: EventData;
  protected eventprojets: EventProjet[] | null | undefined;

  protected allEvents: EventData[] = [];
  protected eventTypes: EventTypes[] = [];
  protected isEventDetailEdit: boolean = false;

  protected form!: FormGroup;
  protected loading = false;
  protected popupPosition = { top: 'inherit', left: 'inherit' };
  protected isPositionEdit: boolean = false;
  protected isDegre: boolean = false;

  protected epsgIsOk: boolean | undefined = undefined;
  protected epsgCodes: any[] = [];
  protected filteredEpsgCodes: any[] = [];
  protected selectedEpsg?: SelectedEpsg;
  protected epsgIndex: EpsgIndex = {};
  protected position_EPSG_REFERENCE_OL?: CustomCoordinate;
  protected positionEpsgSite?: CustomCoordinate;
  protected C1: string = 'X';
  protected C2: string = 'Y';
  protected C3: string = 'Z';

  @Output() close = new EventEmitter<PopupCloseMode>();
  @Output() ok = new EventEmitter<EventData>();
  currentMenu$: Observable<string | null> | undefined;
  menuOptions = MENU_OPTIONS;

  protected devicesExample: string[] = ['mobile', 'tablet', 'desktop'];
  private unsubscribe$ = new Subject<void>();
  protected typeahead$ = new Subject<string>();


  constructor(
    protected currentStateService: CurrentStateService,
    private eventService: EventService,
    private fonctionsService: FonctionsService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any, private translateService: TranslateService
  ) {
    this.epsgIndex = this.parseEpsgIndex(epsgIndexData);
    this.epsgCodes = Object.keys(this.epsgIndex).map(code => ({
      id: code,
      text: `${this.epsgIndex[code].name} (${code})`
    }));

    this.initForm();
  }

  initForm() {
    this.form = new FormGroup({
      eventName: new FormControl(null, [Validators.required, Validators.minLength(2)]),
      eventType: new FormControl(null, [Validators.required]),
      projetLabel: new FormControl(null, [Validators.required]),
      diameter: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      burden: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      spacing: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      holes: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      total: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      detonator: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      stemming: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      mic: new FormControl(null, [Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      startDate: new FormControl(null, [validDateValidator(), Validators.required]),
      endDate: new FormControl(null, validDateValidator()),
      x: new FormControl(null, [Validators.required, Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      y: new FormControl(null, [Validators.required, Validators.pattern(/^-?\d+(\.\d+)?$/)]),
      z: new FormControl(0),
      epsg: new FormControl(''),
      id: new FormControl(null),
    });
  }

  private formatLocalDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  updateForm(eventData: EventData) {
    console.log('updateForm', eventData);
    
    let startDate = eventData.startDate ? this.formatLocalDateForInput(new Date(eventData.startDate)) : null;
    let endDate = eventData.endDate ? this.formatLocalDateForInput(new Date(eventData.endDate)) : null;
    let eventProjet: EventProjet =
    {
      projetLabel: eventData.projetLabel,
      id: eventData.projetId

    };

    this.form.patchValue({
      eventName: eventData.name,
      projetLabel: eventProjet,
      eventType: eventData.eventTypeLabel,
      startDate: startDate,
      endDate: endDate,
      x: eventData.positionCoordinates.x,
      y: eventData.positionCoordinates.y,
      z: eventData.positionCoordinates.z,
      id: eventData.id,
      diameter: eventData.holes?.diameter,
      burden: eventData.holes?.burden,
      spacing: eventData.holes?.spacing,
      holes: eventData.holes?.holes,
      total: eventData.charges?.total,
      detonator: eventData.charges?.detonator,
      stemming: eventData.charges?.stemming,
      mic: eventData.charges?.mic,
    });
  }

  ngOnInit(): void {

    this.initProjets();
    if (this.data.currentEvent && this.data.currentSite) {
      this.isEventDetailEdit = true;
      this.updateForm(this.data.currentEvent);
      this.setSite(this.data.currentSite);
    }

    this.filteredEpsgCodes = [];
    this.typeahead$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => (this.loading = true)),
        switchMap(term => this.searchEpsg(term))
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: results => {
          this.filteredEpsgCodes = [...results];
          setTimeout(() => {
            this.loading = false;
          }, 1000);
        },
        error: () => {
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  initProjets() {
    this.eventprojets = this.currentStateService.getCurrentEvents()
      .map(event => ({
        id: event.projetId,
        projetLabel: event.projetLabel
      } as EventProjet))
      .filter((value, index, self) =>
        self.findIndex(v => v.projetLabel === value.projetLabel) === index
      );

    this.eventTypes = this.currentStateService.getCurrentEvents()
      .map(event => ({
        id: event.eventTypeId,
        eventTypeLabel: event.eventTypeLabel
      } as EventTypes))
      .filter((value, index, self) =>
        self.findIndex(v => v.eventTypeLabel === value.eventTypeLabel) === index
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dialog.closeAll();
    if (this.typeahead$) {
      this.typeahead$.complete();
    }
  }

  setPositionEPSG_REFERENCE_OL(customCoordinate: CustomCoordinate) {
    this.position_EPSG_REFERENCE_OL = customCoordinate;
  }

  setPositionEpsgSite(customCoordinate: CustomCoordinate) {
    this.positionEpsgSite = customCoordinate;
    this.form?.patchValue({
      x: this.positionEpsgSite.coordX,
      y: this.positionEpsgSite.coordY
    });
  }

  setSite(site: Site) {
    this.site = site;
    this.searchEpsg(site.epsg)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: result => {
          this.selectedEpsg = result[0];
          if (this.selectedEpsg) {
            this.epsgIsOk = true;
          }
        }
      });
  }

  setIsDegre(isDegre: boolean) {
    if (isDegre) {
      this.C1 = 'Latitude';
      this.C2 = 'Longitude';
      this.C3 = 'Altitude';
    } else {
      this.C1 = 'X';
      this.C2 = 'Y';
      this.C3 = 'Z';
    }
  }

  setIsEdit(isEdit: boolean) {
    this.isPositionEdit = isEdit;
    this.popupPosition = { top: 'inherit', left: '5rem' };

    let currentEvent = this.currentStateService.getCurrentEvent();

    if (!currentEvent) {
      console.error('Selected source is null');
      return;
    }

    this.updateForm(currentEvent);
  }

  protected onClose(closeMode: PopupCloseMode = PopupCloseMode.CLOSE) {
    this.dialog.closeAll();
    this.close.emit(closeMode);
  }

  submit(): void {
    let isEdition = this.isPositionEdit || this.isEventDetailEdit;
    this.form?.markAllAsTouched();
    const siteId = this.currentStateService.getCurrentSite()?.siteId;
    if (siteId && this.form?.valid) {
      this.loading = true;
      let request = this.getEventData(this.form);
      console.log('request', request);
      
      this.eventService.createOrUpdateVibrationEvent(siteId, request, isEdition).subscribe({
        next: (createdEvent: ApiResponse<EventData>) => {
          this.loading = false;
          this.afterEventCreation(createdEvent.data, isEdition);
        },
        error: (error) => {
          this.loading = false;
          this.fonctionsService.launch_toast(false, isEdition ? this.translateService.instant('ERRORS.CAN_NOT_UPDATE_EVENT') : this.translateService.instant('ERRORS.CAN_NOT_CREATE_EVENT'));
        }
      });
    } else {
      this.form?.markAllAsTouched();
    }
  }

  afterEventCreation(event: EventData, isEdition: boolean) {
    this.onClose(PopupCloseMode.CREATED);
    this.currentStateService.addEvent(event, this.isPositionEdit);
    this.currentStateService.setNewEventCreated(event);
    this.fonctionsService.launch_toast(true, !isEdition ? this.translateService.instant('SUCCESS.SOURCE_CREATED') : this.translateService.instant('SUCCESS.SOURCE_UPDATED'));
    this.currentStateService.setCurrentEvent(event);
  }

  getEventData(form: FormGroup) {
    let projectId = this.eventprojets.find(p => p.projetLabel == form.get('projetLabel')?.value?.projetLabel)?.id;
    let eventId = this.eventTypes.find(p => p.eventTypeLabel == form.get('eventType')?.value?.eventTypeLabel)?.id;

    const parseNumber = (value: any) => (value === '' ? null : value);
    let detonator = parseNumber(form.get('detonator')?.value);
    let stemming = parseNumber(form.get('stemming')?.value);
    let mic = parseNumber(form.get('mic')?.value);
    let total = parseNumber(form.get('total')?.value);

    let charges = (detonator == null && stemming == null && mic == null && total == null)
      ? null
      : { detonator, stemming, mic, total };

    let burden = parseNumber(form.get('burden')?.value);
    let diameter = parseNumber(form.get('diameter')?.value);
    let holesCount = parseNumber(form.get('holes')?.value);
    let spacing = parseNumber(form.get('spacing')?.value);

    let holes = (burden == null && diameter == null && holesCount == null && spacing == null)
      ? null
      : { burden, diameter, holes: holesCount, spacing };

    let event: EventData = {
      id: form.get('id')?.value,
      name: form.get('eventName')?.value,
      eventTypeLabel: form.get('eventType')?.value?.eventTypeLabel,
      projetLabel: form.get('projetLabel')?.value?.projetLabel,
      projetId: projectId,
      eventTypeId: eventId,
      startDate: form.get('startDate')?.value,
      endDate: form.get('endDate')?.value == "" ? null : form.get('startDate')?.value,
      positionCoordinates: {
        x: form.get('x')?.value,
        y: form.get('y')?.value,
        z: form.get('z')?.value,
        epsg: 'EPSG:' + this.site.epsg
      },
      charges: charges,
      holes: holes
    };
    return event;
  }


  searchEpsg(term: string) {
    if (term === '' || !term) {
      return of([]);
    }
    const results = this.epsgCodes.filter(code => code.text.toLowerCase().includes(term.toLowerCase()));
    return of(results);
  }

  initEpsgFromSite(epsg: string): Observable<any[]> {
    return of(epsg).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => this.searchEpsg(searchTerm))
    );
  }

  protected compareItems(item1: any, item2: any): boolean {
    return item1 && item2 ? item1 === item2 : item1 === item2;
  }

  parseEpsgIndex(data: any): EpsgIndex {
    const parsedIndex: EpsgIndex = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const item = data[key];
        parsedIndex[key] = {
          name: item.name || 'Unknown',
          proj4: item.proj4 || null,
          wkt: item.wkt || null,
          bbox: Array.isArray(item.bbox) ? item.bbox : [],
          unit: item.unit || 'Unknown',
          area: item.area || 'Unknown',
          accuracy: typeof item.accuracy === 'number' ? item.accuracy : null
        };
      }
    }
    return parsedIndex;
  }

}
